import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../api";
import swal from "sweetalert";
import { Row, Col, Card, Button, Form, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";

const ExpenseAdd = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const projectFromUrl = searchParams.get("project") || "";
 const expenseTypeFromUrl = searchParams.get("expenseType") || "";

 const [workOrders, setWorkOrders] = useState([]);
 const [expenseTypes, setExpenseTypes] = useState([]);
 const [vendors, setVendors] = useState([]);
 const [loading, setLoading] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [selectedFiles, setSelectedFiles] = useState([]);

 const COMPANY_STATE = "Maharashtra"; // Fallback default

 const getWorkOrderOfficeState = (woId) => {
   const targetId = woId || formData.workOrder;
   const selectedWo = workOrders.find((w) => (w._id || "").toString() === (targetId || "").toString());
   if (!selectedWo) return "Maharashtra";
   const office = selectedWo.project?.office || selectedWo.office || {};
   let st = office.state || "";
   if (!st && office.address) {
     const addr = office.address.toLowerCase();
     if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
     else if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
   }
   return st || "Maharashtra";
 };

 const [formData, setFormData] = useState({
 expenseType: expenseTypeFromUrl || "",
    isRecurring: (expenseTypeFromUrl || "").toLowerCase().includes("recurring"),
    recurringFrequency: "Monthly",
    amount: "",
 expenseDate: new Date().toISOString().split("T")[0],
 paymentMode: "Cash",
 paidTo: "", // Vendor ID
 paidToName: "", // For display or manual entry
 remark: "",
 workOrder: workOrderFromUrl,
 cgst: 0,
 sgst: 0,
 igst: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 totalAmount: 0,
 addGst: false,
 });

 useEffect(() => {
 fetchInitialData();
 }, []);
  useEffect(() => {
    if (workOrderFromUrl) {
      setFormData((prev) => ({
        ...prev,
        workOrder: workOrderFromUrl,
      }));
    }
  }, [workOrderFromUrl]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [woRes, etRes, vRes] = await Promise.all([
        api.get("/work-orders?limit=1000"),
        api.get("/expense-types?status=Active"),
        api.get("/vendors"),
      ]);

      let loadedWorkOrders = [];
      if (woRes.data.success) {
        loadedWorkOrders = woRes.data.data || [];
        setWorkOrders(loadedWorkOrders);
      }
      if (etRes.data.success) {
        setExpenseTypes(etRes.data.data || []);
        if (expenseTypeFromUrl) {
          const matchingType = (etRes.data.data || []).find(
            (t) =>
              t.name.toLowerCase() === expenseTypeFromUrl.toLowerCase() ||
              t.name.toLowerCase().includes(expenseTypeFromUrl.toLowerCase())
          );
          if (matchingType) {
            setFormData((prev) => ({
              ...prev,
              expenseType: matchingType.name,
              isRecurring:
                matchingType.name.toLowerCase().includes("recurring") ||
                matchingType.name.toLowerCase().includes("fixed"),
            }));
          }
        }
      }
      if (vRes.data.success) setVendors(vRes.data.data || []);

      if (workOrderFromUrl) {
        setFormData((prev) => ({ ...prev, workOrder: workOrderFromUrl }));
        if (!loadedWorkOrders.some((wo) => String(wo._id) === String(workOrderFromUrl))) {
          try {
            const singleWoRes = await api.get(`/work-orders/${workOrderFromUrl}`);
            if (singleWoRes.data?.success && singleWoRes.data?.data) {
              setWorkOrders((prev) => [singleWoRes.data.data, ...prev]);
            }
          } catch (e) {
            console.warn("Could not fetch specific work order", e);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching initial data:", err);
      swal("Error", "Failed to load required data", "error");
    } finally {
      setLoading(false);
    }
  };

 const applyGSTByState = (vendorState, woId) => {
 let cgst = 0, sgst = 0, igst = 0;

 if (!vendorState) {
 igst = 18;
 return { cgst, sgst, igst };
 }

 const normalize = (s) => (s || "").toString().trim().toLowerCase();
 const vState = normalize(vendorState);
 const company = normalize(getWorkOrderOfficeState(woId));

 if (vState === company) {
 cgst = 9;
 sgst = 9;
 } else {
 igst = 18;
 }

 return { cgst, sgst, igst };
 };

 useEffect(() => {
 const amount = parseFloat(formData.amount || 0);
 const cgstAmount = parseFloat(formData.cgstAmount || 0);
 const sgstAmount = parseFloat(formData.sgstAmount || 0);
 const igstAmount = parseFloat(formData.igstAmount || 0);

 const total = formData.addGst 
 ? amount + cgstAmount + sgstAmount + igstAmount 
 : amount;

 setFormData((prev) => ({
 ...prev,
 totalAmount: total,
 }));
 }, [formData.amount, formData.cgstAmount, formData.sgstAmount, formData.igstAmount, formData.addGst]);

 const handleInputChange = (e) => {
 const { name, value, type, checked } = e.target;
 
 if (name === "workOrder") {
 const amount = parseFloat(formData.amount || 0);
 const selectedVendor = vendors.find(v => v._id === formData.paidTo || v.name === formData.paidTo);
 const vendorState = selectedVendor?.state || "";
 if (formData.addGst) {
 const { cgst, sgst, igst } = applyGSTByState(vendorState, value);
 setFormData(prev => ({
 ...prev,
 workOrder: value,
 cgst,
 sgst,
 igst,
 cgstAmount: (amount * cgst) / 100,
 sgstAmount: (amount * sgst) / 100,
 igstAmount: (amount * igst) / 100,
 }));
 return;
 }
 setFormData(prev => ({ ...prev, workOrder: value }));
 } else if (name === "paidTo") {
 // Find if it's a known vendor
 const selectedVendor = vendors.find(v => v._id === value || v.name === value);
 const amount = parseFloat(formData.amount || 0);
 
 if (selectedVendor) {
 const vendorState = selectedVendor.state || "";
 const { cgst, sgst, igst } = applyGSTByState(vendorState, formData.workOrder);
 setFormData(prev => ({
 ...prev,
 paidTo: selectedVendor.name, // Save name
 paidToName: selectedVendor.name,
 cgst: prev.addGst ? cgst : 0,
 sgst: prev.addGst ? sgst : 0,
 igst: prev.addGst ? igst : 0,
 cgstAmount: prev.addGst ? (amount * cgst) / 100 : 0,
 sgstAmount: prev.addGst ? (amount * sgst) / 100 : 0,
 igstAmount: prev.addGst ? (amount * igst) / 100 : 0,
 }));
 } else {
 // Manual entry or reset
 const igst = formData.addGst ? 18 : 0;
 setFormData(prev => ({
 ...prev,
 paidTo: value,
 paidToName: value,
 // Reset GST percentages to default IGST 18 if adding GST but vendor unknown
 cgst: 0,
 sgst: 0,
 igst: igst,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: (amount * igst) / 100,
 }));
 }
 } else if (name === "addGst") {
 const amount = parseFloat(formData.amount || 0);
 if (checked) {
 const selectedVendor = vendors.find(v => v._id === formData.paidTo || v.name === formData.paidTo);
 const vendorState = selectedVendor?.state || "";
 const { cgst, sgst, igst } = applyGSTByState(vendorState, formData.workOrder);
 setFormData(prev => ({
 ...prev,
 addGst: true,
 cgst,
 sgst,
 igst,
 cgstAmount: (amount * cgst) / 100,
 sgstAmount: (amount * sgst) / 100,
 igstAmount: (amount * igst) / 100,
 }));
 } else {
 setFormData(prev => ({
 ...prev,
 addGst: false,
 cgst: 0,
 sgst: 0,
 igst: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 }));
 }
 } else if (name === "amount") {
 const amount = parseFloat(value || 0);
 setFormData(prev => ({
 ...prev,
 amount: value,
 cgstAmount: (amount * prev.cgst) / 100,
 sgstAmount: (amount * prev.sgst) / 100,
 igstAmount: (amount * prev.igst) / 100,
 }));
 } else if (["cgst", "sgst"].includes(name)) {
 const rate = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const gstAmt = (amount * rate) / 100;
 setFormData(prev => ({
 ...prev,
 cgst: value,
 sgst: value,
 cgstAmount: gstAmt,
 sgstAmount: gstAmt,
 igst: 0,
 igstAmount: 0,
 }));
 } else if (name === "igst") {
 const rate = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const gstAmt = (amount * rate) / 100;
 setFormData(prev => ({
 ...prev,
 igst: value,
 igstAmount: gstAmt,
 cgst: 0,
 cgstAmount: 0,
 sgst: 0,
 sgstAmount: 0,
 }));
 } else if (["cgstAmount", "sgstAmount"].includes(name)) {
 const gstAmt = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const rate = amount > 0 ? ((gstAmt / amount) * 100).toFixed(2) : 0;
 setFormData(prev => ({
 ...prev,
 cgstAmount: value,
 sgstAmount: value,
 cgst: rate,
 sgst: rate,
 igst: 0,
 igstAmount: 0,
 }));
 } else if (name === "igstAmount") {
 const gstAmt = parseFloat(value || 0);
 const amount = parseFloat(formData.amount || 0);
 const rate = amount > 0 ? ((gstAmt / amount) * 100).toFixed(2) : 0;
 setFormData(prev => ({
 ...prev,
 igstAmount: value,
 igst: rate,
 cgst: 0,
 cgstAmount: 0,
 sgst: 0,
 sgstAmount: 0,
 }));
 } else if (name === "expenseType") {
      const selectedType = expenseTypes.find(t => (t.name || "").toLowerCase() === (value || "").toLowerCase());
      const isRecurring = Boolean((value || "").toLowerCase().includes("recurring") || selectedType?.category === "Recurring");
      setFormData(prev => ({
        ...prev,
        expenseType: value,
        isRecurring: isRecurring,
        recurringFrequency: isRecurring ? (prev.recurringFrequency || selectedType?.recurringFrequency || "Monthly") : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
 };

 const handleFileChange = (e) => {
 const incomingFiles = Array.from(e.target.files || []);
 setSelectedFiles((prev) => {
 const merged = [...prev, ...incomingFiles];
 const seen = new Set();
 return merged.filter((file) => {
 const key = `${file.name}::${file.size}::${file.lastModified}`;
 if (seen.has(key)) return false;
 seen.add(key);
 return true;
 });
 });
 e.target.value = "";
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 setSubmitting(true);
 const data = new FormData();
 Object.keys(formData).forEach((key) => {
 data.append(key, formData[key]);
 });
      if (workOrderFromUrl) {
        data.set("workOrder", workOrderFromUrl);
      }
 selectedFiles.forEach((file) => {
 data.append("bill", file);
 });

 if (selectedFiles.length === 0) {
 data.delete("bill");
 }

 // Ensure totalAmount and GST amounts are correctly appended
 data.set("subTotal", formData.amount);
 data.set("totalAmount", formData.totalAmount);
 data.set("cgstAmount", formData.cgstAmount);
 data.set("sgstAmount", formData.sgstAmount);
 data.set("igstAmount", formData.igstAmount);
 data.set("gstAmount", formData.cgstAmount + formData.sgstAmount + formData.igstAmount);
 data.set("paidBy", "CANSCORP ENGINEERS PVT. LTD."); // Always company

 const res = await api.post("/expenses", data, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 swal("Success", "Expense added successfully", "success");
      navigate(workOrderFromUrl ? `/debit?workOrder=${workOrderFromUrl}` : "/debit");
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to add expense", "error");
 } finally {
 setSubmitting(false);
 }
 };

 const paymentModes = [
 "Cash", "Bank Transfer", "UPI", "Credit Card", "Cheque", "Other"
 ];

 if (loading) {
 return (
 <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
 <Spinner animation="border" variant="primary" />
 </div>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Add Expense" motherMenu="Accounts" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Add Expense</Card.Title>
 <Button 
 variant="secondary" 
 onClick={() => navigate(-1)}
 >
 Back
 </Button>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 <Row>
 <Col md={formData.isRecurring ? 3 : 6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Expense Type *</Form.Label>
                    <Form.Select
                      name="expenseType"
                      value={formData.expenseType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Type</option>
                      {expenseTypes.map((type) => (
                        <option key={type._id} value={type.name}>
                          {type.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {formData.isRecurring && (
                  <Col md={3} className="mb-3">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center justify-content-between">
                        <span><i className="fa fa-sync-alt text-primary me-1"></i> Recurring Frequency *</span>
                        <span className="badge badge-sm bg-light text-primary font-weight-bold" style={{ fontSize: "10px" }}>Fixed/Editable</span>
                      </Form.Label>
                      <Form.Select
                        name="recurringFrequency"
                        value={formData.recurringFrequency || "Monthly"}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Yearly">Yearly</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Amount (Base) *</Form.Label>
 <Form.Control
 type="number"
 name="amount"
 placeholder="Enter base amount"
 value={formData.amount}
 onChange={handleInputChange}
 required
 />
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Expense Date *</Form.Label>
 <Form.Control
 type="date"
 name="expenseDate"
 value={formData.expenseDate}
 onChange={handleInputChange}
 required
 />
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Payment Mode *</Form.Label>
 <Form.Select
 name="paymentMode"
 value={formData.paymentMode}
 onChange={handleInputChange}
 required
 >
 <option value="">Select Payment Mode</option>
 {paymentModes.map(mode => (
 <option key={mode} value={mode}>{mode}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Paid To *</Form.Label>
 <Form.Control
 list="vendor-list"
 name="paidTo"
 placeholder="Select vendor or type manually"
 value={formData.paidTo}
 onChange={handleInputChange}
 required
 />
 <datalist id="vendor-list">
 {vendors.map(v => (
 <option key={v._id} value={v.name} />
 ))}
 </datalist>
 </Form.Group>
 </Col>
  <Col md={6} className="mb-3">
    <Form.Group>
      <Form.Label>
        Work Order * {workOrderFromUrl && (
          <span className="badge bg-light text-primary ms-2 font-weight-bold">
            <i className="fa fa-lock me-1"></i>Selected WO
          </span>
        )}
      </Form.Label>
      <Form.Select
        name="workOrder"
        value={formData.workOrder || workOrderFromUrl}
        onChange={handleInputChange}
        required
        disabled={Boolean(workOrderFromUrl)}
      >
        {!workOrderFromUrl && <option value="">Select Work Order</option>}
        {(workOrderFromUrl
          ? workOrders.filter((wo) => String(wo._id) === String(workOrderFromUrl))
          : workOrders
        ).map((wo) => (
          <option key={wo._id} value={wo._id}>
            {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}
          </option>
        ))}
        {workOrderFromUrl &&
          !workOrders.some((wo) => String(wo._id) === String(workOrderFromUrl)) && (
            <option value={workOrderFromUrl}>
              Work Order ({workOrderFromUrl})
            </option>
          )}
      </Form.Select>
      {workOrderFromUrl && (
        <Form.Text className="text-muted">
          This expense is locked to this particular Work Order.
        </Form.Text>
      )}
    </Form.Group>
  </Col>
 <Col md={12} className="mb-3">
 <Form.Group>
 <Form.Label>Remark</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 name="remark"
 placeholder="Enter remark..."
 value={formData.remark}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <Form.Group>
 <Form.Label>Upload Bill</Form.Label>
 <Form.Control
 type="file"
 multiple
 accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.csv,.zip,.rar,.7z"
 onChange={handleFileChange}
 />
 <Form.Text className="text-muted">
 You can add multiple bills in one or more selections.
 </Form.Text>
 {selectedFiles.length > 0 && (
 <div className="mt-2 small">
 {selectedFiles.map((file, index) => (
 <div key={`${file.name}-${index}`}>{file.name}</div>
 ))}
 </div>
 )}
 </Form.Group>
 </Col>
 <Col md={6} className="mb-3">
 <div className="d-flex align-items-center mt-4">
 <Form.Check
 type="checkbox"
 id="addGst"
 name="addGst"
 label="Add GST?"
 checked={formData.addGst}
 onChange={handleInputChange}
 />
 </div>
 </Col>

 {formData.addGst && (
 <Fragment>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>CGST %</Form.Label>
 <Form.Control
 type="number"
 name="cgst"
 value={formData.cgst}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>SGST %</Form.Label>
 <Form.Control
 type="number"
 name="sgst"
 value={formData.sgst}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>IGST %</Form.Label>
 <Form.Control
 type="number"
 name="igst"
 value={formData.igst}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>CGST Amount</Form.Label>
 <Form.Control
 type="number"
 name="cgstAmount"
 value={formData.cgstAmount}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>SGST Amount</Form.Label>
 <Form.Control
 type="number"
 name="sgstAmount"
 value={formData.sgstAmount}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 <Col md={4} className="mb-3">
 <Form.Group>
 <Form.Label>IGST Amount</Form.Label>
 <Form.Control
 type="number"
 name="igstAmount"
 value={formData.igstAmount}
 onChange={handleInputChange}
 />
 </Form.Group>
 </Col>
 </Fragment>
 )}

 <Col md={12} className="mt-3">
 <div className="border rounded p-3">
 <Row>
 <Col md={6}>
 <h5>Summary</h5>
 <p className="mb-1">Base Amount: ₹ {parseFloat(formData.amount || 0).toLocaleString("en-IN")}</p>
 {formData.addGst && (
 <Fragment>
 <p className="mb-1">CGST: ₹ {formData.cgstAmount.toLocaleString("en-IN")}</p>
 <p className="mb-1">SGST: ₹ {formData.sgstAmount.toLocaleString("en-IN")}</p>
 <p className="mb-1">IGST: ₹ {formData.igstAmount.toLocaleString("en-IN")}</p>
 </Fragment>
 )}
 <h4 className="mt-2 text-primary">Total Amount: ₹ {formData.totalAmount.toLocaleString("en-IN")}</h4>
 </Col>
 </Row>
 </div>
 </Col>

 <Col md={12} className="mt-4">
 <Button variant="primary" type="submit" disabled={submitting}>
 {submitting ? "Adding..." : "Add Expense"}
 </Button>
 </Col>
 </Row>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default ExpenseAdd;
